import React, { useMemo } from 'react';
import type { Contract } from 'src/types/contract.type';

interface OverviewCardsProps {
    contracts: Contract[];
    projectId: string;
}

export default function OverviewCardsV2({ contracts }: OverviewCardsProps) {
    const stats = useMemo(() => {
        const clientContracts = contracts.filter((c) => c.direction === 'CLIENT');
        const subconContracts = contracts.filter((c) => c.direction === 'SUBCON');

        const totalClientAmount = clientContracts.reduce((acc, c) => acc + Number((c as any).contractAmount ?? 0), 0);
        const totalSubconAmount = subconContracts.reduce((acc, c) => acc + Number((c as any).contractAmount ?? 0), 0);
        
        // 기금액(Received/Paid) 정보는 현재 Contract 타입에 직접 없으므로 0으로 두거나 마일스톤 합계로 계산해야 함
        // 여기서는 구조만 잡고 나중에 마일스톤 데이터와 연동 가능
        const receivedAmount = 0; 

        const margin = totalClientAmount - totalSubconAmount;
        const marginPct = totalClientAmount > 0 ? (margin / totalClientAmount) * 100 : 0;

        return {
            totalClientAmount,
            receivedAmount,
            clientBalance: totalClientAmount - receivedAmount,
            totalSubconAmount,
            subconCount: subconContracts.length,
            margin,
            marginPct,
        };
    }, [contracts]);

    const formatCurrency = (val: number) => {
        return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 }).format(val);
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Card 1: Client Contract Overview */}
            <div className="relative overflow-hidden rounded-2xl border bg-white p-5 shadow-sm transition-all hover:shadow-md">
                <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Client Contract</p>
                    <div className="rounded-full bg-emerald-50 p-1.5 text-emerald-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                </div>
                <div className="mt-4">
                    <h4 className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalClientAmount)}</h4>
                    <p className="text-sm text-gray-500 mt-1">Total Project Value</p>
                </div>
                <div className="mt-4 grid grid-cols-2 border-t pt-3 gap-2">
                    <div>
                        <p className="text-[10px] text-gray-400 uppercase font-bold">Received</p>
                        <p className="text-xs font-semibold text-emerald-600">{formatCurrency(stats.receivedAmount)}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-400 uppercase font-bold">Balance</p>
                        <p className="text-xs font-semibold text-gray-700">{formatCurrency(stats.clientBalance)}</p>
                    </div>
                </div>
                <div className="absolute -right-4 -bottom-4 h-16 w-16 opacity-5">
                    <svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                </div>
            </div>

            {/* Card 2: Subcon Contract Summary */}
            <div className="relative overflow-hidden rounded-2xl border bg-white p-5 shadow-sm transition-all hover:shadow-md">
                <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Subcon Commitments</p>
                    <div className="rounded-full bg-blue-50 p-1.5 text-blue-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                    </div>
                </div>
                <div className="mt-4">
                    <h4 className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalSubconAmount)}</h4>
                    <p className="text-sm text-gray-500 mt-1">{stats.subconCount} Active Subcontractors</p>
                </div>
                <div className="mt-4 border-t pt-3">
                    <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">Execution Rate</p>
                        <p className="text-xs font-bold text-blue-600">
                            {stats.totalClientAmount > 0 ? ((stats.totalSubconAmount / stats.totalClientAmount) * 100).toFixed(1) : 0}%
                        </p>
                    </div>
                    <div className="mt-2 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div 
                            className="h-full bg-blue-500 rounded-full" 
                            style={{ width: `${Math.min(100, stats.totalClientAmount > 0 ? (stats.totalSubconAmount / stats.totalClientAmount) * 100 : 0)}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Card 3: Upcoming Actions */}
            <div className="relative overflow-hidden rounded-2xl border bg-white p-5 shadow-sm transition-all hover:shadow-md">
                <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Upcoming Milestones</p>
                    <div className="rounded-full bg-amber-50 p-1.5 text-amber-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                </div>
                <div className="mt-4 space-y-2">
                    <div className="flex items-center gap-2 rounded-lg bg-gray-50 p-2 border border-gray-100">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        <span className="text-xs font-medium truncate">No immediate actions</span>
                    </div>
                    <p className="text-[10px] text-gray-400 text-center">Next 30 days coverage</p>
                </div>
            </div>

            {/* Card 4: Financial Health */}
            <div className="relative overflow-hidden rounded-2xl border bg-white p-5 shadow-sm transition-all hover:shadow-md">
                <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Project Health</p>
                    <div className={`rounded-full p-1.5 ${stats.marginPct > 10 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                    </div>
                </div>
                <div className="mt-4">
                    <h4 className={`text-2xl font-bold ${stats.margin >= 0 ? 'text-gray-900' : 'text-rose-600'}`}>
                        {formatCurrency(stats.margin)}
                    </h4>
                    <p className="text-sm text-gray-500 mt-1">Estimated Project Margin</p>
                </div>
                <div className="mt-4 border-t pt-3 flex items-center gap-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${stats.marginPct > 10 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                        {stats.marginPct.toFixed(1)}% Margin
                    </span>
                </div>
            </div>
        </div>
    );
}
